import { useState, useEffect } from "react";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Upload, ShieldAlert, ShieldX, Loader2, FileText, AlertCircle } from "lucide-react";

const DOC_TYPES = [
  { id: "national_id", label: "بطاقة الهوية الوطنية", required: true },
  { id: "residence",   label: "بطاقة السكن",          required: false },
  { id: "passport",    label: "جواز السفر (بديل عن البطاقتين)", required: false },
];

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function KYC() {
  const { t } = useLang();
  const { user, refreshUser } = useAuth();
  const [files, setFiles] = useState({}); // { national_id: File, residence: File, passport: File }
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  const reload = async () => {
    const r = await api.get("/kyc/status");
    setSubmissions(r.data.submissions || []);
  };
  useEffect(() => { reload(); }, []);

  const onFileChange = (docType) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("الملف يجب أن يكون PDF فقط");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("الحجم أكبر من 8MB");
      return;
    }
    setFiles((p) => ({ ...p, [docType]: f }));
  };

  const submitOne = async (docType) => {
    const f = files[docType];
    if (!f) return;
    setLoading(true);
    try {
      const b64 = await readAsBase64(f);
      await api.post("/kyc/submit", { doc_type: docType, file_base64: b64, file_name: f.name });
      toast.success("✓ تم رفع المستند");
      setFiles((p) => ({ ...p, [docType]: null }));
      await refreshUser();
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const status = user?.kyc_status || "not_submitted";
  const statusMap = {
    approved: { icon: ShieldCheck, color: "text-[#00E676]", bg: "bg-[#00E676]/10 border-[#00E676]/40", label: t("kycApproved") },
    pending: { icon: ShieldAlert, color: "text-[#00E5FF]", bg: "bg-[#00E5FF]/10 border-[#00E5FF]/40", label: t("kycPending") },
    rejected: { icon: ShieldX, color: "text-red-400", bg: "bg-red-500/10 border-red-500/40", label: t("kycRejected") },
    not_submitted: { icon: ShieldAlert, color: "text-[#8FA3BD]", bg: "bg-[#1A2D4D] border-[#1A2D4D]", label: t("kycNotSubmitted") },
  };
  const s = statusMap[status];
  const StatusIcon = s.icon;

  const docMap = {};
  submissions.forEach((sub) => { if (!docMap[sub.doc_type]) docMap[sub.doc_type] = sub; });

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-bold text-cyan-gradient mb-2">{t("kyc")}</h1>
      <p className="text-sm text-[#A3B3AB] mb-6">{t("trustDesc")}</p>

      <div className={`p-4 rounded-xl border flex items-center gap-3 mb-6 ${s.bg}`} data-testid="kyc-status-badge">
        <StatusIcon className={`w-5 h-5 ${s.color}`} />
        <div className="flex-1">
          <div className={`font-semibold ${s.color}`}>{s.label}</div>
          {submissions.length > 0 && submissions[0].admin_note && (
            <div className="text-xs text-[#8FA3BD] mt-1">ملاحظة الأدمن: {submissions[0].admin_note}</div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bento-card p-5 mb-6 border-[#FF9500]/30">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#FF9500] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-[#FF9500] mb-2">المستمسكات المطلوبة:</div>
            <ul className="text-[#8FA3BD] space-y-1 text-xs leading-relaxed">
              <li>• <span className="text-white">بطاقة الهوية الوطنية</span> + <span className="text-white">بطاقة السكن</span></li>
              <li>• أو <span className="text-white">جواز السفر</span> (بديل عن البطاقتين)</li>
              <li>• صيغة <span className="text-[#00E5FF] font-semibold">PDF فقط</span> • أقصى 8MB لكل ملف</li>
              <li>• كل مستند يُراجع يدوياً من الأدمن (لا AI)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Upload area per doc type */}
      <div className="space-y-4">
        {DOC_TYPES.map((dt) => {
          const sub = docMap[dt.id];
          const file = files[dt.id];
          const approved = sub?.status === "approved";
          const rejected = sub?.status === "rejected";
          return (
            <div key={dt.id} className="bento-card p-5" data-testid={`kyc-section-${dt.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#00E5FF]" />
                    {dt.label}
                  </div>
                  {sub && (
                    <div className="text-xs mt-1">
                      <span className={`px-2 py-0.5 rounded-full ${approved ? "bg-[#00E676]/15 text-[#00E676]" : rejected ? "bg-red-500/15 text-red-400" : "bg-[#00E5FF]/15 text-[#00E5FF]"}`}>
                        {sub.status}
                      </span>
                      {sub.admin_note && <span className="text-[#8FA3BD] ms-2">— {sub.admin_note}</span>}
                    </div>
                  )}
                </div>
                {!approved && (
                  <label className="cursor-pointer">
                    <input type="file" accept="application/pdf" className="hidden"
                      onChange={onFileChange(dt.id)} data-testid={`kyc-file-${dt.id}`} />
                    <span className="text-xs px-3 py-2 rounded-lg border border-[#1A2D4D] hover:border-[#00E5FF] inline-flex items-center gap-1.5">
                      <Upload className="w-3 h-3" /> اختر PDF
                    </span>
                  </label>
                )}
              </div>
              {file && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#00E5FF] flex-1 truncate">{file.name}</span>
                  <Button onClick={() => submitOne(dt.id)} disabled={loading}
                    className="btn-cyan h-8 px-3" data-testid={`kyc-submit-${dt.id}`}>
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "رفع"}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
